import Api from "../../../../../core/services/api/api";
import {createUUId} from "../../../../../core/services/utils/utils";
import {applyQtySerialRules, parseDelimitedList, toDistinctSerials, toPartNumberUpper,} from "../../../../views/panel/operator/settings/import/engine/transformers/shared";
import {AdjustmentPreventionInvalidityCode} from "./invalidity-codes";

/**
 * Builds a human-readable error message from invalidity codes.
 * @param {string[]} codes
 * @returns {string}
 */
export const buildAdjustmentImportErrorMessage = (codes = []) => {
    const labels = [];
    if (codes.includes(AdjustmentPreventionInvalidityCode.INVALID_PART)) {
        labels.push('Invalid Part Number');
    }
    if (codes.includes(AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE)) {
        labels.push('Invalid/Missing Warehouse Code');
    }
    if (codes.includes(AdjustmentPreventionInvalidityCode.SERIAL_COUNT_MISMATCH)) {
        labels.push('Serial Count Does Not Match Quantity');
    }
    if (codes.includes(AdjustmentPreventionInvalidityCode.DUPLICATE_PART_ROW)) {
        labels.push('Duplicate Part + Warehouse Row');
    }
    return labels.join(', ');
};

/**
 * Resolves adjustment direction from a signed quantity.
 * @param {number} quantity
 * @returns {'add'|'remove'|null}
 */
const getDirection = (quantity) => {
    if (!Number.isFinite(quantity) || quantity === 0) {
        return null;
    }
    return quantity > 0 ? 'add' : 'remove';
};

/**
 * Builds a duplicate-group key for part + warehouse + direction.
 * @param {string} partNo
 * @param {string|null|undefined} warehouseCode
 * @param {'add'|'remove'|null} direction
 * @returns {string|null}
 */
const buildDuplicateKey = (partNo, warehouseCode, direction) => {
    if (!partNo || !warehouseCode || !direction) {
        return null;
    }
    return `${partNo}::${String(warehouseCode).toUpperCase()}::${direction}`;
};

/**
 * Reads a ValidatePartNumbers map with case-insensitive keys (API may echo request or DB casing).
 * @param {Record<string, boolean>|null|undefined} partValidity
 * @param {string} partNoUpper
 * @returns {boolean}
 */
const isPartNumberValid = (partValidity, partNoUpper) => {
    if (!partNoUpper || !partValidity || typeof partValidity !== 'object') {
        return false;
    }
    if (partValidity[partNoUpper]) {
        return true;
    }
    return Object.entries(partValidity).some(
        ([key, valid]) => valid && String(key).trim().toUpperCase() === partNoUpper,
    );
};

/**
 * Indexes getPartInfoWithPartNumber results by uppercase part number (API returns DB casing).
 * @param {Array<*>|null|undefined} partInfoList
 * @returns {Record<string, *>}
 */
const indexPartInfoByUpperPartNo = (partInfoList) => {
    /** @type {Record<string, *>} */
    const byUpper = {};
    for (const info of partInfoList ?? []) {
        const key = toPartNumberUpper(info?.partNo);
        if (key) {
            byUpper[key] = info;
        }
    }
    return byUpper;
};

/**
 * Validates mapped CSV rows for inventory adjustment import.
 * Follows the phased rowState contract: accumulate codes, never abort the batch,
 * emit preventativeIssues, then split usable rows into add/remove.
 *
 * @param {Array<Object>} parsedRows
 * @param {{ ignoredRowIndices?: number[] }} [options]
 * @returns {Promise<{
 *   parsedRows: Array<Object>,
 *   preventativeIssues: Array<{rowIndex: number, originalRow: Object, invalidityCodes: string[]}>,
 *   addItems: Array<Object>,
 *   removeItems: Array<Object>,
 *   invalidEntries: Array<Object>,
 *   validEntries: Array<Object>,
 * }|null>}
 */
export const validateAdjustmentImport = async (parsedRows, options = {}) => {
    const ignoredSet = new Set(
        (options.ignoredRowIndices ?? []).map((i) => Number(i)).filter((i) => !Number.isNaN(i)),
    );
    const rows = (parsedRows ?? []).map((item) => (item ? {...item} : item));

    /** @type {{index: number, item: *, partNoUpper: string, warehouseCode: string|null, quantity: number, serialNos: string[], distinctSerials: string[], codes: string[]}[]} */
    const rowState = rows.map((item, index) => {
        const serialNos = parseDelimitedList(item?.serialNos);
        const quantity = Number(item?.quantity);
        return {
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNo),
            warehouseCode: item?.warehouseCode ?? null,
            quantity: Number.isFinite(quantity) ? quantity : 0,
            serialNos,
            distinctSerials: toDistinctSerials(serialNos),
            codes: [],
        };
    });

    // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────────
    for (const rs of rowState) {
        if (!rs.item || ignoredSet.has(rs.index)) {
            continue;
        }
        // Empty part numbers are dropped silently (legacy) — skip all checks
        if (!rs.partNoUpper) {
            continue;
        }
        if (!rs.warehouseCode) {
            rs.codes.push(AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE);
        }
        if (rs.distinctSerials.length > 0) {
            rs.codes.push(
                ...applyQtySerialRules({
                    qty: Math.abs(rs.quantity),
                    distinctSerialCount: rs.distinctSerials.length,
                }),
            );
        }
    }

    // ── Phase 2: duplicate part + warehouse + direction ─────────────────────────
    /** @type {Map<string, number[]>} */
    const duplicateGroups = new Map();
    for (const rs of rowState) {
        if (!rs.item || ignoredSet.has(rs.index) || !rs.partNoUpper || !rs.warehouseCode) {
            continue;
        }
        const direction = getDirection(rs.quantity);
        const key = buildDuplicateKey(rs.partNoUpper, rs.warehouseCode, direction);
        if (!key) {
            continue;
        }
        const group = duplicateGroups.get(key) ?? [];
        group.push(rs.index);
        duplicateGroups.set(key, group);
    }
    for (const indices of duplicateGroups.values()) {
        if (indices.length < 2) {
            continue;
        }
        for (const index of indices) {
            rowState[index].codes.push(AdjustmentPreventionInvalidityCode.DUPLICATE_PART_ROW);
        }
    }

    // ── Phase 3: remote part validation ─────────────────────────────────────────
    const partsToValidate = [
        ...new Set(
            rowState
                .filter((rs) => rs.item && !ignoredSet.has(rs.index) && rs.partNoUpper)
                .map((rs) => rs.partNoUpper),
        ),
    ];

    /** @type {Record<string, boolean>} */
    let partValidity = {};
    if (partsToValidate.length > 0) {
        const validationResponse = await Api.validatePartNumbers({
            partNumbers: partsToValidate,
        });
        if (!validationResponse?.resultFlag) {
            return null;
        }
        partValidity = validationResponse.data ?? {};
    }

    for (const rs of rowState) {
        if (!rs.item || ignoredSet.has(rs.index) || !rs.partNoUpper) {
            continue;
        }
        if (!isPartNumberValid(partValidity, rs.partNoUpper)) {
            rs.codes.push(AdjustmentPreventionInvalidityCode.INVALID_PART);
        }
    }

    // ── Phase 4: part info enrichment for rows that still look usable ───────────
    const partsNeedingInfo = [
        ...new Set(
            rowState
                .filter((rs) => {
                    if (!rs.item || ignoredSet.has(rs.index) || !rs.partNoUpper) {
                        return false;
                    }
                    const uniqueCodes = [...new Set(rs.codes)];
                    return !uniqueCodes.includes(AdjustmentPreventionInvalidityCode.INVALID_PART)
                        && !uniqueCodes.includes(AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE);
                })
                .map((rs) => rs.partNoUpper),
        ),
    ];

    /** @type {Record<string, *>} */
    let partInfoByPartNo = {};
    if (partsNeedingInfo.length > 0) {
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: partsNeedingInfo,
        });
        if (!partInfoResponse?.resultFlag) {
            return null;
        }
        // API returns DB casing; index by uppercase so CSV / autocomplete scalars match.
        partInfoByPartNo = indexPartInfoByUpperPartNo(partInfoResponse.data);
    }

    for (const rs of rowState) {
        if (!rs.item || ignoredSet.has(rs.index) || !rs.partNoUpper) {
            continue;
        }
        const uniqueCodes = [...new Set(rs.codes)];
        if (uniqueCodes.includes(AdjustmentPreventionInvalidityCode.INVALID_PART)
            || uniqueCodes.includes(AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE)) {
            continue;
        }
        if (!partInfoByPartNo[rs.partNoUpper]) {
            rs.codes.push(AdjustmentPreventionInvalidityCode.INVALID_PART);
        }
    }

    // ── Emit preventativeIssues ─────────────────────────────────────────────────
    const preventativeIssues = [];
    for (const rs of rowState) {
        if (!rs.item || ignoredSet.has(rs.index) || !rs.partNoUpper) {
            continue;
        }
        const uniqueCodes = [...new Set(rs.codes)];
        if (!uniqueCodes.length) {
            continue;
        }
        preventativeIssues.push({
            rowIndex: rs.index,
            originalRow: rs.item,
            invalidityCodes: uniqueCodes,
        });
    }

    const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

    // ── Split usable rows into add / remove ─────────────────────────────────────
    const addItems = [];
    const removeItems = [];
    const validEntries = [];
    const invalidEntries = [];

    for (const rs of rowState) {
        if (!rs.item) {
            continue;
        }
        // Drop empty part numbers (legacy)
        if (!rs.partNoUpper) {
            continue;
        }

        if (ignoredSet.has(rs.index) || issueIndexSet.has(rs.index)) {
            invalidEntries.push({
                ...rs.item,
                id: createUUId(true),
                serialNos: rs.distinctSerials,
                error: {
                    partNo: rs.codes.includes(AdjustmentPreventionInvalidityCode.INVALID_PART),
                    warehouseCode: rs.codes.includes(AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE),
                    serialCountMismatch: rs.codes.includes(AdjustmentPreventionInvalidityCode.SERIAL_COUNT_MISMATCH),
                    duplicatePartRow: rs.codes.includes(AdjustmentPreventionInvalidityCode.DUPLICATE_PART_ROW),
                    ignored: ignoredSet.has(rs.index),
                },
                errorMessage: ignoredSet.has(rs.index)
                    ? 'Skipped by user'
                    : buildAdjustmentImportErrorMessage([...new Set(rs.codes)]),
            });
            continue;
        }

        const info = partInfoByPartNo[rs.partNoUpper];
        const enriched = {
            ...rs.item,
            id: createUUId(true),
            partNo: rs.partNoUpper,
            partId: info.id,
            parentCategoryId: info.parentCategoryId,
            parentCategoryTitle: info.parentCategoryTitle,
            serialNos: rs.distinctSerials,
            quantity: rs.quantity,
        };

        validEntries.push(enriched);

        if (rs.quantity > 0) {
            addItems.push(enriched);
        } else if (rs.quantity < 0) {
            removeItems.push({
                ...enriched,
                quantity: Math.abs(rs.quantity),
            });
        }
        // quantity === 0 is silently dropped (legacy)
    }

    return {
        parsedRows: rows,
        preventativeIssues,
        addItems,
        removeItems,
        invalidEntries,
        validEntries,
    };
};
